import { create } from 'zustand'

import { clearBranchPref, getBranchPref, setBranchPref } from './branch-prefs'

/**
 * Which branch the customer is on, for the components that render it.
 *
 * **The cookie is the source of truth and this store is not a second one.** The inline script in
 * `index.html` runs before any bundle exists and reads the cookie to decide whether to redirect; a
 * `localStorage` store could not be consulted at that point, and `localStorage` is per-origin so it
 * cannot be shared between `pizzamajstro.sk` and `nesvady.pizzamajstro.sk` — which is the entire reason
 * the preference lives in a cookie scoped to the common parent domain.
 *
 * So this is **not persisted**. It reads `getBranchPref()` on creation, which already reads the cookie
 * with the per-origin mirror behind it, and every write goes through `setBranchPref` so the cookie, the
 * mirror and this store cannot drift. Persisting it separately would create a fourth copy of one fact.
 */

export interface BranchState {
  /** The chosen branch's host, normalised, or `null` before a choice. */
  host: string | null
  /**
   * Whether the picker is open *by request* — the header chip and the footer link both reopen it.
   *
   * Separate from the gate's own decision to ask, because the two are different situations: the gate
   * asks a first-time visitor and will not take no for an answer, and this is somebody who already chose
   * and wants to change. Legacy keeps the same two modes in one store, and the picker reads which.
   */
  pickerOpen: boolean
  open: () => void
  closePicker: () => void
  /** `parentDomain` is the brand's common parent, or `null` for a brand whose branches share none. */
  choose: (host: string, parentDomain: string | null) => void
  forget: (parentDomain: string | null) => void
}

export const useBranchStore = create<BranchState>((set) => ({
  host: getBranchPref(),
  pickerOpen: false,
  open: () => set({ pickerOpen: true }),
  closePicker: () => set({ pickerOpen: false }),
  choose: (host, parentDomain) => {
    setBranchPref(host, parentDomain)
    set({ host: getBranchPref(), pickerOpen: false })
  },
  forget: (parentDomain) => {
    clearBranchPref(parentDomain)
    set({ host: null })
  },
}))
